import Head from "next/head"
import { useRouter } from "next/router"
import { GetStaticProps } from "next/types"

import Footer from "../../storyblok/static/Footer"

import PetTransportCost from "../../sections/cost-to-ship/PetTransportCost"
import PetTransportFactors from "../../sections/cost-to-ship/PetTransportFactors"
import PetCostBreakdown from "../../sections/cost-to-ship/PetCostBreakdown"
import PetAreaTransporter from "../../sections/cost-to-ship/PetAreaTransporter"
import HowItWorks from "../../sections/cost-to-ship/HowItWorks"
import CostToShipHeader from "../../sections/cost-to-ship/CostToShipHeader"
import PetTransportAnimals from "../../sections/cost-to-ship/PetTransportAnimals"
import ItemTransportCost from "../../sections/cost-to-ship/ItemTransportCost"
import PetTransportReviews from "../../sections/cost-to-ship/PetTransportReviews"
import ItemTransportReviews from "../../sections/cost-to-ship/ItemTransportReviews"
import MeetDriver from "../../sections/homepage/HomepageMeetDrivers"
import OtherAreas from "../../sections/cost-to-ship/OtherAreas"
import MSAsJson from "../../constants/cost-to-ship/MSAsJson"
import { urlifyName } from "../../constants/stateCityDictionary"
import petTransportsJson from "../../constants/cost-to-ship/petTransportsJson"
import itemTransportsJson from "../../constants/cost-to-ship/itemTransportsJson"
import areaTransportersJson from "../../constants/cost-to-ship/areaTransporters"
import { ReviewsContextProvider } from "../../context/ReviewsContext"
import LazyYoutube from "@/components/testimonials/LazyYoutube"

interface ICostToShipProps {
  petTransports: typeof petTransportsJson
  itemTransports: typeof itemTransportsJson
  MSA: typeof MSAsJson[number]
  areaTransporters: typeof areaTransportersJson[0]["content"]
}

export default function CostToShipCity({
  petTransports,
  itemTransports,
  MSA,
  areaTransporters,
}: ICostToShipProps) {
  const hyphenIndex = MSA.name.indexOf("-")
  const metroName = MSA.name.slice(
    0,
    hyphenIndex >= 0 ? hyphenIndex : undefined,
  )

  const router = useRouter()

  const { msa } = router.query

  const stateCode = MSA.stateCodes[0]

  const title = `Cost to ship to ${metroName}, ${stateCode} at affordable rates`

  const description = `Cost to ship a dog, car, package, box or any precious item, to ${metroName}`

  const image =
    "https://citizenshipper.com/_next/image?url=%2Fimages%2Fboy-dog-polaroid.webp&w=828&q=75"

  const url = new URL(`https://citizenshipper.com/cost-to-ship/${msa}`)
  const cleanURL = `${url.origin}${url.pathname}`

  return (
    <ReviewsContextProvider>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={cleanURL} />
        <meta name="image" content={image} />
        <meta itemProp="name" content={title} />
        <meta itemProp="image" content={image} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:site" content="@CitizenShipper" />
        <meta name="twitter:image:src" content={image} />
        <meta name="og:title" content={title} />
        <meta name="og:description" content={description} />
        <meta name="og:image" content={image} />
        <meta
          name="og:url"
          content={`https://citizenshipper.com/cost-to-ship/${msa}`}
        />
        <meta name="og:site_name" content="CitizenShipper" />
        <script
          type="application/ld+json"
          key="msa-schema"
          dangerouslySetInnerHTML={{
            __html: `{
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": "Affordable Local Shipping Service to ${metroName}, ${stateCode}",
            "image": "https://citizenshipper.com/_next/image?url=%2Fimages%2Fcar-polaroid.webp&w=828&q=75",
            "description": "Affordable Local Shipping Service that delivers anywhere within the US.",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "7879",
              "reviewCount": "",
              "bestRating": "5",
              "worstRating": "1",
              "itemReviewed": {
                "@type": "Service",
                "url": "https://citizenshipper.com/cost-to-ship/${msa}",
                "name": "Affordable Local Shipping Service"
              },
              "name": "Affordable Local Shipping Service"
            },
            "offers": {
                  "@type": "AggregateOffer",
                  "lowPrice": "80",
                  "priceCurrency": "USD"
                },
              "review": [
                {
                  "@type": "Review",
                  "author": {
                    "@type": "Person",
                    "name": "Laurie Dilday"
                  },
                  "description": "I highly recommend CitizenShipper. I used them and got A+ service. What a great experience.",
                  "url": "https://goo.gl/maps/zxmksCK94PtnqGyD9",
                  "publisher": {
                    "@type": "Organization",
                    "name": "Google"
                  }
                },
                {
                  "@type": "Review",
                  "author": {
                    "@type": "Person",
                    "name": "Kechia Poullard"
                  },
                  "description": "My experience with this company was awesome! My driver delivered my pet safe and sound! She also sent pictures and videos of her at restroom stops! I would recommend her to family and friends and use this service again!",
                  "url": "https://goo.gl/maps/MvRub3JoXurxdZsL8",
                  "publisher": {
                    "@type": "Organization",
                    "name": "Google"
                  }
                },
                {
                  "@type": "Review",
                  "author": {
                    "@type": "Person",
                    "name": "Francisco Calderon"
                  },
                  "description": "CitizenShipper is a great site and did an amazing job to help me get a car shipped in short notice after my first shipper didn’t show up they were quick to get me a quote and get my vehicle shipped with a great price as well!!",
                  "url": "https://www.trustpilot.com/reviews/618bfe9bfb38891f5f07b5d8",
                  "publisher": {
                    "@type": "Organization",
                    "name": "TrustPilot"
                  }
                }
              ]
              }`,
          }}
        ></script>
        <script
          type="application/ld+json"
          key="msa-breadcrumb-schema"
          dangerouslySetInnerHTML={{
            __html: `{
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                          {
                "@type": "ListItem",
                "position": 1,
                "name": "Cost to Ship - All Cities",
                "item": "https://citizenshipper.com/cost-to-ship"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Cost to ship to ${metroName}, ${stateCode}",
                "item": "https://citizenshipper.com/cost-to-ship/${msa}"
              }
                          ]
            }`,
          }}
        ></script>
      </Head>
      <CostToShipHeader metroName={metroName} stateCode={stateCode} />
      <div className="2xl:px-44 xl:px-36 lg:px-32 md:px-24 sm:px-12 px-4 md:mt-10 mx-auto max-w-5xl">
        <div className="p-2 mt-6 mb-0 mx-auto max-w-4xl">
          <div className="md:p-1 rounded-md border border-color-gold">
            <LazyYoutube videoId="FB4XfBMqfEM" title="Cost to Ship Video" />
          </div>
        </div>
      </div>
      <PetTransportCost petTransports={petTransports} />
      <PetTransportAnimals />
      <PetTransportFactors metroName={metroName} />
      <PetTransportReviews />
      <PetCostBreakdown costOfLiving={MSA.costOfLiving} metroName={metroName} />
      <PetAreaTransporter areaTransporters={areaTransporters} />
      <HowItWorks />
      <ItemTransportCost
        itemTransports={itemTransports}
        metroName={metroName}
        stateCode={stateCode}
      />
      <ItemTransportReviews />
      <MeetDriver />
      <OtherAreas metroName={metroName} />
      <Footer noBlok />
    </ReviewsContextProvider>
  )
}

export async function getStaticPaths() {
  const paths = MSAsJson.map(({ name }) => ({
    params: {
      msa: urlifyName(name),
    },
  }))

  return {
    paths,
    fallback: false,
  }
}

interface CostToShipProps extends GetStaticProps {
  params: { msa: string }
}

export async function getStaticProps({ params: { msa } }: CostToShipProps) {
  const petTransports = petTransportsJson.filter(
    (transport) => urlifyName(transport.msa_name_deliver) === msa,
  )

  const itemTransports = itemTransportsJson.filter(
    (transport) => urlifyName(transport.msa_name_deliver) === msa,
  )

  const MSA = MSAsJson.find(({ name }) => urlifyName(name) === msa)!

  const areaTransporters =
    areaTransportersJson.filter(
      (item) => item.url === `/cost-to-ship/${msa}`,
    )[0]?.content ?? ""

  return {
    props: {
      petTransports,
      itemTransports,
      MSA,
      areaTransporters,
    },
  }
}
